import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFileAlt } from "react-icons/fa";
import { FaRegTrashCan, FaPlus } from "react-icons/fa6";

const initialFiles = [
  {
    name: "Contrat de travail_janvier.pdf",
    date: "Jan 6, 2022",
  },
  {
    name: "Curriculum Vitae_2025.pdf",
    date: "Jan 6, 2022",
  },
];

const Documents = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [files, setFiles] = useState(initialFiles);
  const [deleting, setDeleting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState("");
  const fileInputRef = useRef();

  const handleDelete = (index) => {
    setDeleting(index);
  };

  const confirmDelete = () => {
    setFiles((prev) => prev.filter((_, i) => i !== deleting));
    setToast(t('employeeProfile.documents.deleted'));
    setDeleting(null);
    setTimeout(() => setToast(""), 2000);
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setTimeout(() => {
      setFiles((prev) => [
        {
          name: file.name,
          date: new Date().toLocaleDateString(dateLocale, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
        },
        ...prev,
      ]);
      setUploading(false);
      setToast(t('employeeProfile.documents.added'));
      setTimeout(() => setToast(""), 2000);
    }, 1200);
  };

  return (
    <div className="h-full bg-gray-50 p-4">
      <div className="container mx-auto max-w-5xl p-6 bg-white rounded-2xl shadow-lg mt-2 relative">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-[--color-primary]">{t('employeeProfile.documents.title')}</h1>
          <button
            className="flex items-center gap-2 bg-[--color-primary] hover:bg-[--bg-hover] text-white px-4 py-2 rounded-lg shadow transition"
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
          >
            <FaPlus />
            <span>{t('employeeProfile.documents.add')}</span>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleUpload}
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          />
        </div>

        {uploading && (
          <div className="flex justify-center my-4">
            <div className="loader"></div>
          </div>
        )}

        {files.length === 0 && !uploading && (
          <div className="flex flex-col items-center py-16 text-gray-400">
            <FaFileAlt className="text-6xl mb-4" />
            <p className="text-lg">{t('employeeProfile.documents.empty')}</p>
          </div>
        )}

        {files.length > 0 && (
          <div className="overflow-x-auto">
            <table className="table-auto w-full border-collapse rounded-lg">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">{t('employeeProfile.documents.name')}</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-semibold">{t('employeeProfile.documents.lastModified')}</th>
                  <th className="px-4 py-3 text-center text-gray-600 font-semibold">{t('employeeProfile.documents.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file, index) => (
                  <tr
                    key={index}
                    className="border-t border-gray-100 hover:bg-blue-50 transition"
                  >
                    <td className="px-4 py-3 flex items-center gap-3">
                      <span className="bg-blue-100 p-2 rounded-full">
                        <FaFileAlt className="text-[--color-primary]" />
                      </span>
                      <span className="text-gray-800 font-medium">{file.name}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{file.date}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        className="btn btn-ghost btn-sm text-gray-400 hover:text-red-600 transition"
                        aria-label={t('employeeProfile.documents.deleteAria', { name: file.name })}
                        onClick={() => handleDelete(index)}
                      >
                        <FaRegTrashCan />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal de confirmation suppression */}
        {deleting !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-sm w-full">
              <h2 className="text-xl font-semibold mb-4 text-[--color-primary]">{t('employeeProfile.documents.deleteConfirmTitle')}</h2>
              <p className="mb-6 text-gray-600">
                {t('employeeProfile.documents.deleteConfirmText')}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                  onClick={() => setDeleting(null)}
                >
                  {t('employeeProfile.documents.cancel')}
                </button>
                <button
                  className="px-4 py-2 rounded bg-red-500 hover:bg-red-600 text-white"
                  onClick={confirmDelete}
                >
                  {t('employeeProfile.documents.confirmDelete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast notification */}
        {toast && (
          <div className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
            {toast}
          </div>
        )}
      </div>
    </div>    
  );
};

export default Documents;